import { c as _c } from "react/compiler-runtime";
import { Box, Button, Center, Heading, HStack, IconButton, Input, InputGroup, Text, VStack } from '@chakra-ui/react';
import { UserAvatar } from '@src/components/sco/Account/UserAvatar';
import { AdvisorsLoadMore } from '@src/components/sco/Advisors/shared/AdvisorsLoadMore';
import { SectionScroll } from '@src/components/sco/Advisors/shared/SectionScroll';
import { ButtonLink } from '@src/components/sco/ButtonLink';
import { CloseIcon, SearchIcon } from '@src/components/sco/icons';
import { useEffect, useMemo, useState } from 'react';
export type EngagementSearchListItem = {
  key: string;
  name: string;
  href: string;
  userId?: string | null;
  subtitle?: string | null;
  /** Extra strings included in client-side name search (company, etc.). */
  searchText?: string;
};
type EngagementSearchListProps = {
  title: string;
  subtitle?: string;
  items: EngagementSearchListItem[];
  maxItems?: number;
  searchPlaceholder?: string;
  emptyMessage?: string;
  noMatchMessage?: string;
  /**
   * When true, search is controlled by the parent and filtering happens server-side.
   * Parent must pass `search` / `onSearchChange` and already-filtered `items`.
   */
  serverSide?: boolean;
  search?: string;
  onSearchChange?: (value: string) => void;
  /** Server-side pagination (1-based). When pageCount > 1, prev/next controls render. */
  page?: number;
  pageCount?: number;
  itemCount?: number;
  onPageChange?: (page: number) => void;
  loading?: boolean;
};
const matchesSearch = (item: EngagementSearchListItem, query: string) => {
  if (!query) {
    return true;
  }
  const haystack = [item.name, item.subtitle, item.searchText].filter(Boolean).join(' ').toLowerCase();
  return haystack.includes(query);
};
export const EngagementSearchList = t0 => {
  const $ = _c(65);
  const {
    title,
    subtitle,
    items,
    maxItems: t1,
    searchPlaceholder: t2,
    emptyMessage: t3,
    noMatchMessage: t4,
    serverSide: t5,
    search: controlledSearch,
    onSearchChange,
    page: t6,
    pageCount: t7,
    itemCount,
    onPageChange,
    loading: t8
  } = t0;
  const maxItems = t1 === undefined ? 100 : t1;
  const searchPlaceholder = t2 === undefined ? "Search by name..." : t2;
  const emptyMessage = t3 === undefined ? "No results yet." : t3;
  const noMatchMessage = t4 === undefined ? "No results match your search." : t4;
  const serverSide = t5 === undefined ? false : t5;
  const page = t6 === undefined ? 1 : t6;
  const pageCount = t7 === undefined ? 1 : t7;
  const loading = t8 === undefined ? false : t8;
  const [localSearch, setLocalSearch] = useState("");
  const search = serverSide ? controlledSearch ?? "" : localSearch;
  let t9;
  if ($[0] !== onSearchChange || $[1] !== serverSide) {
    t9 = value => {
      if (serverSide) {
        onSearchChange?.(value);
      } else {
        setLocalSearch(value);
      }
    };
    $[0] = onSearchChange;
    $[1] = serverSide;
    $[2] = t9;
  } else {
    t9 = $[2];
  }
  const setSearch = t9;
  let t10;
  if ($[3] !== search) {
    t10 = search.trim().toLowerCase();
    $[3] = search;
    $[4] = t10;
  } else {
    t10 = $[4];
  }
  const query = t10;
  const [visibleCount, setVisibleCount] = useState(maxItems);
  let t11;
  if ($[5] !== maxItems) {
    t11 = () => {
      setVisibleCount(maxItems);
    };
    $[5] = maxItems;
    $[6] = t11;
  } else {
    t11 = $[6];
  }
  let t12;
  if ($[7] !== maxItems || $[8] !== query) {
    t12 = [maxItems, query];
    $[7] = maxItems;
    $[8] = query;
    $[9] = t12;
  } else {
    t12 = $[9];
  }
  useEffect(t11, t12);
  let t13;
  bb0: {
    if (serverSide) {
      t13 = items;
      break bb0;
    }
    let t14;
    if ($[10] !== items || $[11] !== query || $[12] !== visibleCount) {
      let t15;
      if ($[14] !== query) {
        t15 = item => matchesSearch(item, query);
        $[14] = query;
        $[15] = t15;
      } else {
        t15 = $[15];
      }
      t14 = items.filter(t15).slice(0, visibleCount);
      $[10] = items;
      $[11] = query;
      $[12] = visibleCount;
      $[13] = t14;
    } else {
      t14 = $[13];
    }
    t13 = t14;
  }
  const visibleItems = t13;
  let t14;
  bb1: {
    if (serverSide) {
      t14 = itemCount ?? items.length;
      break bb1;
    }
    let t15;
    if ($[16] !== items || $[17] !== query) {
      let t16;
      if ($[19] !== query) {
        t16 = item_0 => matchesSearch(item_0, query);
        $[19] = query;
        $[20] = t16;
      } else {
        t16 = $[20];
      }
      t15 = items.filter(t16);
      $[16] = items;
      $[17] = query;
      $[18] = t15;
    } else {
      t15 = $[18];
    }
    t14 = t15.length;
  }
  const totalMatching = t14;
  const clientHasMore = !serverSide && totalMatching > visibleCount;
  const showPagination = serverSide && pageCount > 1 && !!onPageChange;
  const hasActiveSearch = !!query;
  let t15;
  bb2: {
    if (loading && items.length === 0) {
      t15 = "Loading...";
      break bb2;
    }
    if (items.length === 0 && !hasActiveSearch) {
      t15 = emptyMessage;
      break bb2;
    }
    if (totalMatching === 0) {
      t15 = noMatchMessage;
      break bb2;
    }
    if (serverSide) {
      t15 = `Showing ${visibleItems.length}${totalMatching > visibleItems.length ? ` of ${totalMatching}` : ""} result${visibleItems.length === 1 ? "" : "s"}${pageCount > 1 ? ` · Page ${page} of ${pageCount}` : ""}`;
      break bb2;
    }
    t15 = `Showing ${visibleItems.length}${totalMatching > visibleItems.length ? ` of ${totalMatching}` : ""} result${visibleItems.length === 1 ? "" : "s"}`;
  }
  const statusText = t15;
  let t16;
  if ($[21] !== title) {
    t16 = <Heading size="2xl">{title}</Heading>;
    $[21] = title;
    $[22] = t16;
  } else {
    t16 = $[22];
  }
  let t17;
  if ($[23] !== subtitle) {
    t17 = subtitle && <Text color="colorPalette.fg/60" fontSize="md">{subtitle}</Text>;
    $[23] = subtitle;
    $[24] = t17;
  } else {
    t17 = $[24];
  }
  let t18;
  if ($[25] !== t16 || $[26] !== t17) {
    t18 = <VStack align="stretch" gap={1}>{t16}{t17}</VStack>;
    $[25] = t16;
    $[26] = t17;
    $[27] = t18;
  } else {
    t18 = $[27];
  }
  let t19;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <SearchIcon color="colorPalette.fg/60" />;
    $[28] = t19;
  } else {
    t19 = $[28];
  }
  let t20;
  if ($[29] !== search || $[30] !== setSearch) {
    t20 = search ? <IconButton aria-label="Clear search" size="xs" variant="ghost" onClick={() => setSearch("")}><CloseIcon boxSize={2} /></IconButton> : undefined;
    $[29] = search;
    $[30] = setSearch;
    $[31] = t20;
  } else {
    t20 = $[31];
  }
  let t21;
  if ($[32] !== setSearch) {
    t21 = e => setSearch(e.target.value);
    $[32] = setSearch;
    $[33] = t21;
  } else {
    t21 = $[33];
  }
  let t22;
  if ($[34] !== search || $[35] !== searchPlaceholder || $[36] !== t21) {
    t22 = <Input paddingLeft={10} placeholder={searchPlaceholder} size="md" type="text" value={search} onChange={t21} />;
    $[34] = search;
    $[35] = searchPlaceholder;
    $[36] = t21;
    $[37] = t22;
  } else {
    t22 = $[37];
  }
  let t23;
  if ($[38] !== t20 || $[39] !== t22) {
    t23 = <InputGroup startElement={t19} endElement={t20}>{t22}</InputGroup>;
    $[38] = t20;
    $[39] = t22;
    $[40] = t23;
  } else {
    t23 = $[40];
  }
  let t24;
  if ($[41] !== statusText) {
    t24 = <Text color="colorPalette.fg/50" fontSize="xs">{statusText}</Text>;
    $[41] = statusText;
    $[42] = t24;
  } else {
    t24 = $[42];
  }
  let t25;
  if ($[43] !== emptyMessage || $[44] !== hasActiveSearch || $[45] !== items.length || $[46] !== noMatchMessage || $[47] !== visibleItems) {
    t25 = items.length === 0 && !hasActiveSearch ? <Center paddingY={10}><Text color="colorPalette.fg/50">{emptyMessage}</Text></Center> : visibleItems.length === 0 ? <Center paddingY={10}><Text color="colorPalette.fg/50">{noMatchMessage}</Text></Center> : <VStack align="stretch" gap={1} width="full">{visibleItems.map(_temp)}</VStack>;
    $[43] = emptyMessage;
    $[44] = hasActiveSearch;
    $[45] = items.length;
    $[46] = noMatchMessage;
    $[47] = visibleItems;
    $[48] = t25;
  } else {
    t25 = $[48];
  }
  let t26;
  if ($[49] !== loading || $[50] !== onPageChange || $[51] !== page || $[52] !== pageCount || $[53] !== showPagination) {
    t26 = showPagination && <HStack gap={4} justify="center" paddingTop={4}><Button disabled={page <= 1 || loading} size="sm" variant="outline" onClick={() => onPageChange?.(Math.max(1, page - 1))}>← Previous</Button><Text color="colorPalette.fg/60" fontSize="sm">Page {page} of {pageCount}</Text><Button disabled={page >= pageCount || loading} size="sm" variant="outline" onClick={() => onPageChange?.(Math.min(pageCount, page + 1))}>Next →</Button></HStack>;
    $[49] = loading;
    $[50] = onPageChange;
    $[51] = page;
    $[52] = pageCount;
    $[53] = showPagination;
    $[54] = t26;
  } else {
    t26 = $[54];
  }
  let t27;
  if ($[55] !== clientHasMore || $[56] !== maxItems) {
    t27 = clientHasMore && <AdvisorsLoadMore hasNextPage={true} onLoadMore={() => setVisibleCount(count => count + maxItems)} />;
    $[55] = clientHasMore;
    $[56] = maxItems;
    $[57] = t27;
  } else {
    t27 = $[57];
  }
  let t28;
  if ($[58] !== t18 || $[59] !== t23 || $[60] !== t24 || $[61] !== t25 || $[62] !== t26 || $[63] !== t27) {
    t28 = <SectionScroll maxWidth="3xl">{t18}{t23}{t24}{t25}{t26}{t27}</SectionScroll>;
    $[58] = t18;
    $[59] = t23;
    $[60] = t24;
    $[61] = t25;
    $[62] = t26;
    $[63] = t27;
    $[64] = t28;
  } else {
    t28 = $[64];
  }
  return t28;
};
function _temp(item_1) {
  return <ButtonLink key={item_1.key} height="auto" href={item_1.href} justifyContent="flex-start" paddingX={3} paddingY={3} variant="ghost" width="full"><Box alignItems="center" as="span" display="flex" gap={3} minWidth={0} width="full"><UserAvatar avatarProps={{
        size: "sm"
      }} noLink={true} userId={item_1.userId || undefined} /><VStack align="stretch" as="span" flex={1} gap={0} minWidth={0}><Text fontWeight="medium" lineClamp={1} textAlign="left">{item_1.name}</Text>{item_1.subtitle && <Text color="colorPalette.fg/55" fontSize="sm" lineClamp={1} textAlign="left">{item_1.subtitle}</Text>}</VStack></Box></ButtonLink>;
}